// view/overflowBar.js — a connected button bar (.topo2-btn-listnav) that does not fit its container folds elements
// into an extra bar behind a "+" button (plan bugfix_2026-10-14_polishing-patch, phase overflow-leiste).
//
//   attachOverflow({ bar, priority, moreLabel, moreButtonClass?, container?, fits?, observe? }) → { update, destroy }
//   attachOverflowOnMount({ bar, within?, … same options }) → { destroy } — for a bar built before it is mounted: waits
//       until the bar is in the page, then attaches with container = bar.closest(within) (default: its parent).
//
// priority: groups of the bar's elements in the order they move out, e.g. [[start], [fullscreen], [back, forward]];
// elements in no group always stay. On every size change of the container all elements come back first, then group by
// group moves out until fits() holds — the result depends on the width only, no hysteresis needed. The "+" (last in the
// bar) is in the bar only while something is out — a hidden one would still be :last-child and take the outer frame
// from the real last segment. It opens the extra bar below it, above when there is no room below in the window.
// Elements are moved, not copied — their handlers stay; only moved ones are put back (a focused field keeps its focus).
// A click outside or Escape closes the extra bar.
//
// The module knows nothing about its consumers; what moves and in which order is theirs. Colours of the extra bar:
// --topo2-overflow-bg on the bar (topo2/overflow-bar.css).

import { createDisposers } from '../core/disposers.js';

const DEFAULT_MORE_CLASS = 'noshadow topo2-btn topo2-btn--icon topo2-btn--solid topo2-btn--small';

function observeSize(el, onResize) {
    const observer = new ResizeObserver(onResize);
    observer.observe(el);
    return () => observer.disconnect();
}

export function attachOverflow({
    bar,
    priority,
    moreLabel,
    moreButtonClass = DEFAULT_MORE_CLASS,
    container = bar.parentElement,
    fits = () => container.scrollWidth <= container.clientWidth,
    observe = observeSize,
}) {
    const disposers = createDisposers();
    const on = (node, ev, fn) => { node.addEventListener(ev, fn); disposers.add(() => node.removeEventListener(ev, fn)); };

    const original = [...bar.children];   // the bar's own order — elements come back in it

    const wrap = document.createElement('span');
    wrap.className = 'topo2-overflow';
    const more = document.createElement('button');
    more.type = 'button';
    more.className = `${moreButtonClass} topo2-overflow-more`;
    more.title = moreLabel;
    more.setAttribute('aria-label', moreLabel);
    more.setAttribute('aria-haspopup', 'true');
    more.setAttribute('aria-expanded', 'false');
    more.innerHTML = '<span class="topo2-ic topo2-ic--plus"></span>';
    const panel = document.createElement('span');
    panel.className = 'topo2-overflow-panel topo2-btn-listnav';
    panel.hidden = true;
    wrap.append(more, panel);

    const isOpen = () => !panel.hidden;
    const close = () => {
        panel.hidden = true;
        more.setAttribute('aria-expanded', 'false');
    };
    const open = () => {
        wrap.classList.remove('is-up');
        panel.hidden = false;
        more.setAttribute('aria-expanded', 'true');
        // Down by default; up when the extra bar would leave the window at the bottom (e.g. a popup footer).
        if (panel.getBoundingClientRect().bottom > window.innerHeight) {
            wrap.classList.add('is-up');
        }
    };

    // Puts every moved element back before the next of its original followers that is still in the bar.
    const restore = () => {
        original.forEach((el, i) => {
            if (el.parentNode === bar) { return; }
            const next = original.slice(i + 1).find((n) => n.parentNode === bar);
            bar.insertBefore(el, next ?? null);
        });
    };

    function update() {
        close();
        wrap.remove();
        restore();
        if (fits()) { return; }
        bar.append(wrap);   // the "+" needs its room before measuring
        const out = new Set();
        for (const group of priority) {
            group.forEach((el) => out.add(el));
            original.filter((el) => out.has(el)).forEach((el) => panel.append(el));
            if (fits()) { break; }
        }
    }

    on(more, 'click', () => { if (isOpen()) { close(); } else { open(); } });
    on(document, 'pointerdown', (ev) => { if (isOpen() && !wrap.contains(ev.target)) { close(); } });
    on(document, 'keydown', (ev) => { if (ev.key === 'Escape' && isOpen()) { close(); } });

    // Only the container is watched: its width is the limit, and moving elements does not change it (watching the bar
    // too would re-trigger on its own moves).
    disposers.add(observe(container, update));

    return {
        update,
        destroy() {
            disposers.dispose();
            close();
            wrap.remove();
            restore();
        },
    };
}

// A bar gets its size only once it is in the page (the first resize of the bar itself); then the container is known.
// within: a selector for bar.closest() — the bar itself counts (e.g. the audio row is its own container).
export function attachOverflowOnMount({ bar, within = null, observe = observeSize, ...options }) {
    let overflow = null;
    let stopWatching = null;
    stopWatching = observe(bar, () => {
        if (overflow || !bar.isConnected) { return; }
        const container = within ? bar.closest(within) : bar.parentElement;
        if (!container) { return; }
        overflow = attachOverflow({ bar, container, observe, ...options });
        overflow.update();
    });
    return {
        destroy() {
            stopWatching();
            overflow?.destroy();
        },
    };
}
